import { queryOptions, useQuery } from "@tanstack/react-query";
import invariant from "tiny-invariant";
import {
  Address,
  formatUnits,
  getContract,
  PublicClient,
  zeroAddress,
} from "viem";
import { useChainId, usePublicClient } from "wagmi";

import { chainLinkAggregatorConfig } from "shared/config";
import { chainlinkAggregatorV3ABI } from "shared/config/abi";
import { QueryOptions } from "shared/types";

type Data = readonly [
  roundId: bigint,
  answer: bigint,
  startedAt: bigint,
  updatedAt: bigint,
  answeredInRound: bigint,
];

type FetchLatestPriceParams = {
  client: PublicClient;
  address: Address;
  chainId: number;
};

type FetchLatestPriceResult = {
  decimals: number;
  data: Data;
};

const fetchLatestPriceQuery = async (
  params: FetchLatestPriceParams
): Promise<FetchLatestPriceResult> => {
  const { client, address } = params;

  const contract = getContract({
    abi: chainlinkAggregatorV3ABI,
    address,
    client,
  });

  const decimalsPromise = contract.read.decimals();
  const latestRoundDataPromise = contract.read.latestRoundData();

  const [decimals, latestRoundData] = await Promise.all([
    decimalsPromise,
    latestRoundDataPromise,
  ]);

  return {
    decimals,
    data: latestRoundData,
  };
};

type Params = {
  client: PublicClient | undefined;
  addresses: Record<string, Address>;
  chainId: number;
};

const getLatestPriceQueryKey = (params: Omit<Params, "client">) => {
  return ["latest-price", params.addresses, params.chainId];
};

type UseLatestPricesQueryResult = {
  latestPrices: FetchLatestPriceResult[];
};

const getQueryOptions = <TData = UseLatestPricesQueryResult>(
  params: Params,
  options?: QueryOptions<UseLatestPricesQueryResult, unknown, TData>
) => {
  const { client } = params;
  return queryOptions({
    retry: !import.meta.env.DEV,
    queryKey: getLatestPriceQueryKey(params),
    queryFn: async () => {
      invariant(client, "useLatestPriceQuery. client is undefined");

      const filteredAddresses = Object.values(params.addresses).filter(
        (address) => address !== zeroAddress
      );

      const promises = filteredAddresses.map((address) => {
        return fetchLatestPriceQuery({ ...params, address, client });
      });

      const response = await Promise.all(promises);

      return { latestPrices: response };
    },
    staleTime: 1000 * 5,
    enabled: Boolean(client),
    ...options,
  });
};

export const useLatestPricesQuery = <TData = UseLatestPricesQueryResult>(
  options?: QueryOptions<UseLatestPricesQueryResult, unknown, TData>
) => {
  const chainId = useChainId();
  const pc = usePublicClient({ chainId });

  const query = useQuery(
    getQueryOptions<TData>(
      {
        addresses: chainLinkAggregatorConfig[chainId],
        chainId,
        client: pc,
      },
      options
    )
  );

  return query;
};

type Transformer = (price: bigint, decimal: number) => bigint;
type SelectResult = Record<string, ReturnType<typeof withFormatted>>;

const withFormatted = (
  { decimals, data }: FetchLatestPriceResult,
  transformer?: Transformer
) => {
  const price = transformer ? transformer(data[1], decimals) : data[1];
  const formatted = formatUnits(price, decimals);
  return {
    formatted: formatted,
    price,
    decimals,
    raw: { decimals, data },
  };
};

export const createLatestPricesSelect =
  (chainId: number, transformer?: Transformer) =>
  ({ latestPrices }: { latestPrices: FetchLatestPriceResult[] }) => {
    const symbols = Object.keys(chainLinkAggregatorConfig[chainId]);
    const result = symbols.reduce((acc, symbol, index) => {
      acc[symbol] = withFormatted(latestPrices[index], transformer);
      return acc;
    }, {} as SelectResult);

    return result;
  };
